import { UserInfo } from "../interfaces/authentication";
import { ProfilStatus } from "./stamdataProfil";

/**
 * Om sessionen IKKE tilhører en person: et firma-login med MitID Erhverv eller et revisor-login.
 *
 * I sådan en session er `userInfo.id` et Firma- eller Revisor-id. Sider, der slår medlemmet op som
 * person — ID-kort og "Mine oplysninger" — fandt derfor ingen og skrev "din profil er endnu ikke
 * klar", så en advokat, der var logget ind med MitID Erhverv for sin egen virksomhed, fik at vide,
 * at profilen fejlede, når svaret var at logge ind med sit private MitID (ADV-356).
 *
 * Samme regel som for efteruddannelse (`getVisibleApps`, `canAccessDelegatedPath`) og samme
 * fail-open: mangler `identityType`, regnes sessionen for personlig. Værnet ligger i backenden
 * (`POST IdKort` afviser en session uden person), og et svar fra en ældre backend uden feltet må
 * ikke spærre alle advokater ude.
 */
export const erIkkePersonligSession = (
  userInfo: { identityType?: UserInfo["identityType"] } | null | undefined,
): boolean =>
  userInfo?.identityType !== undefined && userInfo.identityType !== "Personal";

/** Hvorfor en side, der kun giver mening for en person, ikke kan vises. */
export type PersonligSideAfvisning = "ikkePersonligSession" | "profilIkkeKlar";

/**
 * Hvilken besked en personlig side (ID-kort, "Mine oplysninger") skal vise i stedet for sit indhold,
 * eller `null`, hvis siden kan vises.
 *
 * Rækkefølgen er pointen (ADV-356). En firma- eller revisor-session har intet person-id, så
 * profil-opslaget svarer ALTID "ikke klar" for den. Blev profilen tjekket først, fik en advokat, der
 * var logget ind med MitID Erhverv, at vide, at profilen fejlede, når svaret var at logge ind med
 * sit private MitID. Sessionstypen kendes fra login, så den afgør det, før profilen er hentet.
 *
 * "profil ikke klar" findes stadig for en personlig session uden Person-række i portalen (ADV-288),
 * og "ukendt" afviser ikke: mens opslaget kører, eller hvis det fejlede, viser siden sit eget
 * indhold og sine egne fejl.
 */
export const getPersonligSideAfvisning = ({
  userInfo,
  profilStatus,
}: {
  userInfo: { identityType?: UserInfo["identityType"] } | null | undefined;
  profilStatus: ProfilStatus;
}): PersonligSideAfvisning | null => {
  if (erIkkePersonligSession(userInfo)) return "ikkePersonligSession";
  if (profilStatus === "ikkeKlar") return "profilIkkeKlar";
  return null;
};
